'use client';

import { useEffect, useRef } from 'react';

import {
  getClientConsentByGroupName,
  getCookie,
  isWebView,
} from '@onyx/cookies';
import { getTelemetrySessionId } from '@onyx/observability-v2/utils';

import type { UseInitObservability } from '../../interface/init';
import {
  restoreSessionProperties,
  setEventProperties,
  setPageProperty,
  setSessionId,
  setSessionProperty,
  setUserId,
} from './properties';

export const IS_WEB_VIEW_EVENT_PROPERTY = 'is_web_view';
export const MS_SESSION_ID_EVENT_PROPERTY = 'ms_session_id';

export const useInitObservability: UseInitObservability = ({
  userId,
  buildId,
} = {}) => {
  const hasInitializedRef = useRef(false);
  const unsubscribeEventPropertiesRef = useRef<(() => void) | undefined>(
    undefined
  );

  useEffect(() => {
    hasInitializedRef.current = false;

    const handle = async () => {
      const { result } = await getClientConsentByGroupName('analytics');
      if (result !== 'given') {
        window.dtrum?.disable();
        return;
      }

      window.dtrum?.enable();

      if (!hasInitializedRef.current) {
        restoreSessionProperties();
        const sessionId = getTelemetrySessionId();
        const isWebViewRequest = isWebView();

        const sessionProperties = {
          isSynthetic: Boolean(getCookie('X-Abuse-Info')),
          isWebView: isWebViewRequest,
          buildId: buildId ?? window.__NEXT_DATA__?.buildId,
          customReferrer: document.referrer,
          utagVersion: window.utag_data?.['ut.version'],
        };

        const pageProperties = {
          'page.name': window.location.pathname,
          'page.title': document.title,
          'page.url.full': window.location.href,
          'page.url.domain': window.location.host,
          'page.url.path': window.location.pathname,
          'page.url.query': window.location.search,
          'page.url.scheme': window.location.protocol.endsWith(':')
            ? window.location.protocol.slice(0, -1)
            : window.location.protocol,
        };

        const eventProperties = {
          [IS_WEB_VIEW_EVENT_PROPERTY]: isWebViewRequest,
          [MS_SESSION_ID_EVENT_PROPERTY]: sessionId,
        };

        userId && setUserId(userId);
        sessionId && setSessionId(sessionId);

        Object.entries(sessionProperties).forEach(([key, value]) =>
          setSessionProperty(key, value)
        );
        Object.entries(pageProperties).forEach(([key, value]) =>
          setPageProperty(key, value)
        );

        unsubscribeEventPropertiesRef.current =
          setEventProperties(eventProperties);

        hasInitializedRef.current = true;
      }
    };

    void handle();
    window.addEventListener('consent.onetrust', handle);

    return () => {
      window.removeEventListener('consent.onetrust', handle);
      unsubscribeEventPropertiesRef.current?.();
      unsubscribeEventPropertiesRef.current = undefined;
    };
  }, [userId, buildId]);
};
